import { Injectable } from '@angular/core';
import { ConfirmDialogRequest, ConfirmDialogComponent, SHIPMENT_CONFIRM_TOKEN } from '../../shared/confirm-dialog/confirm-dialog.component';
import { IMdlCustomDialogConfiguration, MdlDialogService } from '@angular-mdl/core';
import { GridHeader, ActionType } from '../../../sample-tracking/tracking-overview/interfaces/grid';
import { SiteShippingBarcodeDetailsData } from './container-barcode-info';
import * as moment from 'moment';
import { DeleteDialogComponent } from '../../shared/delete-warning/delete-warning-dialog.component';
import { AddressField, Country, ShipmentFormInfo } from './form-field';
import { ShipmentData, ShipmentFormData } from '../../interfaces/shipment-form-data';
import { FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { LocalStorage } from '@ng-idle/core';
import { Settings } from '../../../core';
import { isNil } from 'lodash';
import { DateRangeFilterValue } from '../../../shared/time-frame-filter/timeframe-filter.component';

@Injectable()
export class ShipmentFormService {

    private sessionFormName = 'shipment-form-data';
    private sessionBarcodesList = 'container-barcodes';
    private sessionContainerGrid = 'container-grid-data';
    private sessionContainerTransferData = 'container-transfer-data';
    private sessionFocusData = 'current-focus-data';
    private sessionResumeShipment = 'resume-shipment';
    constructor(
        private readonly dialogService: MdlDialogService,
        private settings: Settings) {

    }

    public isUserEmulated() {

        return !isNil(this.settings.emulatedUserId) ? true : false;
    }

    public showConfirmDialog(dialogRequest: ConfirmDialogRequest, showDeleteDialog = false) {

        const configDialog: IMdlCustomDialogConfiguration = {
            component: showDeleteDialog ? DeleteDialogComponent : ConfirmDialogComponent,
            providers: [{
                provide: SHIPMENT_CONFIRM_TOKEN, useValue: dialogRequest
            }],
            isModal: true,
            classes: showDeleteDialog ? 'container-delete-delete-dialog' : 'st-report-confirmation-dialog',
            styles: { 'border-radius': '30px' },
            clickOutsideToClose: false
        };

        return this.dialogService.showCustomDialog(configDialog);
    }

    public getCountryId(address: Country[], countryName: string) {
        const selectedAddress = address.find(a => a.countryName === countryName);
        if (selectedAddress) {
            return +selectedAddress.countryId || 0;
        }
        return 0;
    }

    public getToLabSiteId(address: AddressField[], orgName: string) {
        const selectedOrg = address.find(a => a.companyName === orgName);
        if (selectedOrg) {
            return +selectedOrg.labSiteId || null;
        }
        return null;
    }

    public getWarningMessage(statusCode: number, data: any = {}) {
        if (statusCode === 1) {
            return {
                message: `This container belongs to an existing shipment.<br>
            (Shipment Date : ${(data.submittedOn) ? moment(data.submittedOn).local().format('DD-MMM-YYYY') : 'NA'};  Airbill Number : ${data.airbillNumber ? data.airbillNumber : 'NA'}).<br>
            Adding the container to the current shipment will transfer it from your previous shipment.`,
                yesButtonText: 'Yes, transfer this container'
            };
        }
        if (statusCode === 2) {
            return {
                message: `Container entered is not expected to be returned to Labcorp.<br>
            Please verify the container should be added to the shipment before continuing.`,
                yesButtonText: 'Yes, Add this container'
            };
        }
        return {
            message: `Container entered is not a Labcorp container.<br>
        Please verify the container should be added to the shipment before continuing.`,
            yesButtonText: 'Yes, Add this container'
        };
    }

    public getShipmentTableHeader(sampleKey = 'barcodeNumber'): GridHeader[] {
        let header: GridHeader[] =
            [
                {
                    displayText: 'Sample',
                    key: sampleKey,
                    sort: null,
                    subFields: [{ key: 'containerLabel1' }, { key: 'containerLabel2' }]
                },
                {
                    displayText: 'Barcode',
                    key: 'userEnteredBarcode',
                    sort: null
                },
                {
                    displayText: 'Expected Return Type',
                    key: 'expectedReturnType',
                    sort: null
                },
                {
                    displayText: 'Expected Return Condition',
                    key: 'expectedReturnCondition',
                    sort: null
                },
                {
                    displayText: 'Protocol',
                    key: 'protocolCode',
                    sort: null
                },
                {
                    displayText: 'Visit Description',
                    key: 'visitDescription',
                    sort: null
                },
                {
                    displayText: 'Actions',
                    key: '',
                    sort: null,
                    hideSort: true,
                    actions: [
                        {
                            description: 'Remove',
                            actionType: ActionType.delete,
                            cssClass: 'action delete',
                            displayText: 'Delete',
                            helpTextClass: 'action helptext'
                        },
                    ]
                }
            ];
        return header;
    }

    public formatShipmentDetails(data: SiteShippingBarcodeDetailsData, checkPermission = true) {
        data.children = [];
        data.barcodeNumber = data.barcodeRaw;
        if (data.accessionNumber && data.containerNumber) {
            data.barcodeNumber = data.accessionNumber + '-' + this.pad(data.containerNumber);
        }
        if (!data.hasPermission && checkPermission) {
            data.barcodeNumber = data.barcodeNumber + ' - No Protocol Access';
            data.cssClass = 'disable-row';
            data.protocolCode = 'No Protocol Access';
            data.visitDescription = 'No Protocol Access - If you need access, please contact your study coordinator';
        }
        data.action = '';
        if (data.statusCode === 3) { // For Non Labcorp Container, set barcode as user entered.
            data.barcodeNumber = data.unFormattedBarcode;
        }
        return data;
    }

    public formatBarcode(barcode: string) {
        if (!barcode) { return null; }
        barcode = barcode.replace('-', '');
        if (barcode.length <= 10) {
            return { accessionNumber: barcode, containerNumber: '', userEnteredBarcode: barcode, formattedBarcode: barcode };
        }
        return this.sliceContainerAndAccession(10, barcode);
    }

    public getLocalStorage() {
        const formValue = localStorage.getItem(this.sessionFormName) as any;
        const barcodes = localStorage.getItem(this.sessionBarcodesList);
        const gridData = localStorage.getItem(this.sessionContainerGrid);
        const transferData = localStorage.getItem(this.sessionContainerTransferData);
        let currentFocus = localStorage.getItem(this.sessionFocusData) as any;
        if (currentFocus) {
            currentFocus = JSON.parse(currentFocus);
        }
        const resumeShipment = localStorage.getItem(this.sessionResumeShipment) as any;
        return { formValue, barcodes, gridData, transferData, currentFocus, resumeShipment };
    }

    public setCurrentFocusData(showShipmentDetails: boolean) {
        const data = {
            showShipmentDetails: showShipmentDetails
        };
        localStorage.setItem(this.sessionFocusData, JSON.stringify(data));
    }

    public setContainerValuesToLocalStorage(containerBarcodes: string[]) {
        localStorage.setItem(this.sessionBarcodesList, containerBarcodes.toString());
    }

    public setGridDataToLocalStorage(gridData: any) {
        localStorage.setItem(this.sessionContainerGrid, JSON.stringify(gridData));
    }

    public setFormValuesToLocalStorage(shipmentFormData: any) {
        localStorage.setItem(this.sessionFormName, JSON.stringify(shipmentFormData));
    }

    public setContainerTransferDataToLocalStorage(containerTransferData: any) {
        localStorage.setItem(this.sessionContainerTransferData, JSON.stringify(containerTransferData));
    }

    public setResumeShipment(isResumeShipment: any) {
        localStorage.setItem(this.sessionResumeShipment, JSON.stringify(isResumeShipment));
    }

    public deleteLocalStorage() {
        localStorage.removeItem(this.sessionFormName);
        localStorage.removeItem(this.sessionBarcodesList);
        localStorage.removeItem(this.sessionContainerGrid);
        localStorage.removeItem(this.sessionContainerTransferData);
        localStorage.removeItem(this.sessionFocusData);
        localStorage.removeItem(this.sessionResumeShipment);
    }

    public formatBarcodeList(barcodeList: string[]) {
        if (!barcodeList || barcodeList.length === 0) { return barcodeList; }
        var formattedList: string[] = [];
        for (let barcode of barcodeList) {
            var barcodeInfo = this.formatBarcode(barcode);
            formattedList.push(barcodeInfo.formattedBarcode);
        }
        return formattedList;
    }

    public loadShippingFromAddressDetails(shipmentFormGroup: any, shipmentData: ShipmentData, countryList: Country[]) {
        shipmentData.fromAddressLine1 = shipmentFormGroup.shippingFromAddress.shippingAddressLine1;
        shipmentData.fromAddressLine2 = shipmentFormGroup.shippingFromAddress.shippingAddressLine2;
        shipmentData.fromAddressLine3 = shipmentFormGroup.shippingFromAddress.shippingAddressLine3 || null;
        shipmentData.fromCity = shipmentFormGroup.shippingFromAddress.shippingCity;
        shipmentData.fromCountryId = this.getCountryId(countryList, shipmentFormGroup.shippingFromAddress.shippingCountry);
        shipmentData.fromOrganizationName = shipmentFormGroup.shippingFromAddress.shippingOrgName;
        shipmentData.fromPhoneNumber = shipmentFormGroup.shippingFromAddress.shippingOrgPhone;
        shipmentData.fromPostalCode = shipmentFormGroup.shippingFromAddress.shippingPostalCode;
        shipmentData.fromStateProvince = shipmentFormGroup.shippingFromAddress.shippingState;
        return shipmentData;
    }

    public loadDestinationAddressDetails(shipmentFormGroup: any, shipmentData: ShipmentData, countryList: Country[], addressList: { key: number, value: string, address: AddressField }[]) {
        shipmentData.toAddressLine1 = shipmentFormGroup.destinationAddress.destinationAddressLine1;
        shipmentData.toAddressLine2 = shipmentFormGroup.destinationAddress.destinationAddressLine2;
        shipmentData.toAddressLine3 = shipmentFormGroup.destinationAddress.destinationAddressLine3 || null;
        shipmentData.toCity = shipmentFormGroup.destinationAddress.destinationCity;
        shipmentData.toCountryId = this.getCountryId(countryList, shipmentFormGroup.destinationAddress.destinationCountry);
        shipmentData.toOrganizationName = shipmentFormGroup.destinationAddress.destinationOrgName;
        shipmentData.toPhoneNumber = shipmentFormGroup.destinationAddress.destinationOrgPhone;
        shipmentData.toPostalCode = shipmentFormGroup.destinationAddress.destinationPostalCode;
        shipmentData.toStateProvince = shipmentFormGroup.destinationAddress.destinationState;
        shipmentData.toLabSiteId = this.getToLabSiteId(addressList.map(da => da.address), shipmentFormGroup.destinationAddress.destinationOrgName);
        return shipmentData;
    }

    public getExistingShipmentWarning(containerTransferReason: { description: string, transferReasonId: number }[]) {
        return {
            message: `You have selected to transfer the container to this shipment.<br>
            Please enter or select the reason for the container transfer.`,
            isWarning: false,
            yesButtonText: 'Continue transfer',
            dropDownData: {
                placeholder: 'Enter or Select Transfer Reason',
                listItem: containerTransferReason.map(ctr => { return { key: ctr.transferReasonId, value: ctr.description }; })
            }

        } as ConfirmDialogRequest;
    }

    public showSaveOrSubmitConfirmation(shipmentName = '', isSaveAction = false): Observable<boolean> {
        if (isSaveAction) {
            return this.showSaveConfirmation(shipmentName);
        }
        return this.showSubmitConfirmation();
    }

    public showSaveShipmentError(): Observable<any> {
        let dialogRequest: ConfirmDialogRequest = {
            message: 'Shipment must be named in order to Save For Later.' +
                '<br> Please enter a Shipment Name. ',
            yesButtonText: 'Next Step',
            noButtonText: 'Continue Editing',
            title: 'Save For Later',
            dropDownData: {
                placeholder: 'Enter Shipment Name',
            }
        };
        return this.showConfirmDialog(dialogRequest).pipe(
            switchMap(dialog => dialog.onHide()),
            map(dialogResult => dialogResult));
    }

    public patchShipmentName(shippingToName: string, shipmentFormGroup: FormGroup) {
        if (!shippingToName || shippingToName.length === 0) { return; }
        shipmentFormGroup.patchValue({ 'destination': 'Enter Custom Address' });
        shipmentFormGroup.get('destinationAddress').get('destinationOrgName').setValue(shippingToName);
    }

    public getContainerTransferData(warningResult: any, data: SiteShippingBarcodeDetailsData) {
        return {
            containerTransferReasonId: warningResult.selectedOption.key,
            otherReason: warningResult.selectedOption.reason,
            siteShipmentContainerId: data.investigatorsiteShipmentContainerId,
            transferedOn: moment().local().format('DD-MMM-YY hh:mm:ss A Z'),
            userEnteredBarcode: data.userEnteredBarcode
        };
    }

    public pad(num: string) {
        if (num.length < 2) {
            num = '0' + num;
        }
        return num;
    }

    public getMonthlyRange(): DateRangeFilterValue {
        return {
            dateBegin: moment().subtract(30, 'd').format('YYYY-MM-DD'),
            dateEnd: moment().format('YYYY-MM-DD'),
            rangeType: 'monthly'
        };
    }

    public applySamplesSearch(term: string, header: GridHeader[], gridData: SiteShippingBarcodeDetailsData[]) {
        term = term.toLowerCase().trim();
        let filterData: SiteShippingBarcodeDetailsData[] = [];
        let searchEnabledColumns = header.filter(t => t.enableSearch);
        for (var rowData of gridData) {
            for (var col of searchEnabledColumns) {
                if (`${rowData[col.key as keyof SiteShippingBarcodeDetailsData]}`.toLowerCase().indexOf(term) !== -1) {
                    filterData.push(rowData);
                    break;
                }
            }
        }
        return filterData;
    }

    private showSubmitConfirmation(): Observable<boolean> {

        let dialogRequest: ConfirmDialogRequest = {
            message: 'Your shipment cannot be edited after submitting.',
            yesButtonText: 'Yes, Submit',
            noButtonText: 'No, keep editing',
            title: 'Submit Shipment',
            reverseButtons: true
        };
        return this.showConfirmDialog(dialogRequest).pipe(
            switchMap(dialog => dialog.onHide()),
            map(dialogResult => dialogResult.isCancelled || false));

    }

    private showSaveConfirmation(shipmentName: string): Observable<boolean> {
        let dialogRequest: ConfirmDialogRequest = {
            message: 'Saved shipments can be resumed later. <br><br> Please note that the shipment - ' + shipmentName + ' is not submitted.',
            yesButtonText: 'Yes, Save this shipment',
            noButtonText: 'No, keep editing',
            title: 'Save Shipment',
            reverseButtons: true
        };
        return this.showConfirmDialog(dialogRequest).pipe(
            switchMap(dialog => dialog.onHide()),
            map(dialogResult => dialogResult.isCancelled || false));
    }

    private sliceContainerAndAccession(index: number, barcode: string) {
        let accession = barcode.slice(0, index);
        let container = barcode.slice(index);
        container = this.pad(container);
        //container = container && container.length > 0 ? container.replace(/^0+/, '') : container;
        //if (container.length === 1) {
        //    let containerNo = Number(container);
        //    if (!containerNo) {
        //        return { accessionNumber: barcode, containerNumber: '', userEnteredBarcode: barcode, formattedBarcode: barcode };
        //    }
        //    return { accessionNumber: accession, containerNumber: container, userEnteredBarcode: barcode, formattedBarcode: accession.concat('0', container) };
        //}
        return { accessionNumber: accession, containerNumber: container, userEnteredBarcode: barcode, formattedBarcode: accession.concat(container) };
    }

}